/**
 * Image Helper Utility
 * Handles image URL formatting and provides fallback images
 */

// Fallback images for different types
export const FALLBACK_IMAGES = {
  product: 'https://images.unsplash.com/photo-1596461404969-9ae70f2830c1?w=800&q=80',
  category: 'https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=800&q=80',
  slider: 'https://images.unsplash.com/photo-1596461404969-9ae70f2830c1?w=1800&q=80',
  user: 'https://ui-avatars.com/api/?background=2F1E14&color=fff&name=User',
};

/**
 * Get proper image URL with fallback
 * @param {string} url - Original image URL
 * @param {string} type - Type of image (product, category, slider, user)
 * @returns {string} - Properly formatted URL or fallback
 */
export const getImageUrl = (url, type = 'product') => {
  // If no URL provided, return fallback
  if (!url) {
    return FALLBACK_IMAGES[type] || FALLBACK_IMAGES.product;
  }

  // If already a full URL (http/https), return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Local upload path (e.g. slider images on the server): same-origin, nginx proxies /uploads to the backend
  if (url.startsWith('/uploads')) {
    return url;
  }

  // If S3 URL pattern (starts with products/, categories/, etc.)
  if (url.includes('/') && !url.startsWith('/')) {
    const S3_BUCKET = process.env.REACT_APP_S3_BUCKET_NAME || 'thrayam-threads-s3';
    const S3_REGION = process.env.REACT_APP_AWS_REGION || 'ap-south-1';
    return `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com/${url}`;
  }

  // If just a filename (like 'cotton-category.jpg'), try backend static endpoint
  if (!url.includes('/')) {
    return `/uploads/${url}`;
  }

  // Default fallback
  return FALLBACK_IMAGES[type] || FALLBACK_IMAGES.product;
};

/**
 * Handle image load error by setting fallback
 * @param {Event} e - Image error event
 * @param {string} type - Type of image for appropriate fallback
 */
export const handleImageError = (e, type = 'product') => {
  const img = e.target;

  // Prevent infinite loop if fallback also fails
  if (img.src === FALLBACK_IMAGES[type]) {
    return;
  }

  console.warn(`Image failed to load: ${img.src}, using fallback`);
  img.src = FALLBACK_IMAGES[type] || FALLBACK_IMAGES.product;
};

/**
 * Get product image with fallback
 * @param {Object} product - Product object
 * @param {number} index - Image index (default 0)
 * @returns {string} - Image URL
 */
export const getProductImage = (product, index = 0) => {
  const imageUrl = product?.images?.[index]?.url || product?.images?.[0]?.url;
  return getImageUrl(imageUrl, 'product');
};

/**
 * Get category image with fallback
 * @param {Object} category - Category object
 * @returns {string} - Image URL
 */
export const getCategoryImage = (category) => {
  return getImageUrl(category?.image, 'category');
};

/**
 * Get slider image with fallback
 * @param {Object} slider - Slider object
 * @returns {string} - Image URL
 */
export const getSliderImage = (slider) => {
  return getImageUrl(slider?.image, 'slider');
};

/**
 * Unsplash resizes and converts on the fly: request only the width the screen
 * needs, as WebP/AVIF (auto=format), optionally cropped to the box's aspect
 * ratio (e.g. '3:1'). Other URLs are returned unchanged.
 */
export const responsiveImage = (url, widths = [480, 768, 1024, 1440], ratio) => {
  if (!/^https:\/\/images\.unsplash\.com\//.test(url || '')) return { src: url };
  const base = url.split('?')[0];
  const at = (w) => `${base}?auto=format&fit=crop&q=70&w=${w}${ratio ? `&ar=${ratio}` : ''}`;
  return { src: at(widths[Math.min(1, widths.length - 1)]), srcSet: widths.map(w => `${at(w)} ${w}w`).join(', ') };
};
